import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Spin } from 'antd';
import {
  FolderOutlined,
  FileOutlined,
  FileZipOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { parseNginxIndex } from '../../utils/parseNginxIndex';
import './mirrorBrowser.css';

/* ── Dosya tipine göre ikon seçici ───────────────────────────── */
function getFileIcon(name) {
  const ext = name.split('.').pop().toLowerCase();
  if (['iso', 'img', 'tar', 'gz', 'xz', 'bz2', 'zip', '7z', 'rar', 'zst'].includes(ext))
    return <FileZipOutlined />;
  if (['txt', 'md', 'log', 'cfg', 'conf', 'ini', 'json', 'xml', 'yaml', 'yml', 'sig', 'sha256sum', 'sha512sum', 'asc'].includes(ext))
    return <FileTextOutlined />;
  if (ext === 'pdf') return <FilePdfOutlined />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext))
    return <FileImageOutlined />;
  return <FileOutlined />;
}

/* ── Ana Bileşen ─────────────────────────────────────────────── */
export default function MirrorBrowser({ path, onPathChange }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchListing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `/mirrors/${path}${path.endsWith('/') || path === '' ? '' : '/'}`;
      const { data } = await axios.get(url, { responseType: 'text' });
      setItems(parseNginxIndex(data));
    } catch (e) {
      setError('Could not load directory contents.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  /* Breadcrumb segmentleri */
  const segments = path.split('/').filter(Boolean);

  const handleSegmentClick = (idx) => {
    const newPath = segments.slice(0, idx + 1).join('/') + '/';
    onPathChange(newPath);
  };

  const handleItemClick = (item) => {
    if (item.isDir) {
      const base = path.endsWith('/') ? path : path + '/';
      onPathChange(base + item.href);
    } else {
      // Dosya — aynı sekmede direkt indirme
      const base = path.endsWith('/') ? path : path + '/';
      window.location.href = `/mirrors/${base}${item.href}`;
    }
  };

  const handleParentClick = () => {
    if (segments.length <= 1) {
      onPathChange('');
      return;
    }
    const newPath = segments.slice(0, -1).join('/') + '/';
    onPathChange(newPath);
  };

  return (
    <div className="mb">
      {/* Breadcrumb + araç çubuğu */}
      <div className="mb__toolbar">
        <button
          className="mb__btn"
          onClick={handleParentClick}
          disabled={segments.length === 0}
          title="Parent directory"
        >
          <ArrowLeftOutlined />
        </button>

        <div className="mb__breadcrumb">
          <button
            className="mb__crumb"
            onClick={() => onPathChange('')}
          >
            mirrors
          </button>
          {segments.map((seg, i) => (
            <React.Fragment key={i}>
              <span className="mb__crumb-sep">/</span>
              <button
                className="mb__crumb"
                onClick={() => handleSegmentClick(i)}
              >
                {seg}
              </button>
            </React.Fragment>
          ))}
        </div>

        <button
          className="mb__btn"
          onClick={fetchListing}
          title="Refresh"
        >
          <ReloadOutlined spin={loading} />
        </button>
      </div>

      {/* İçerik */}
      <div className="mb__body">
        {loading && (
          <div className="mb__loading">
            <Spin size="large" />
            <span>Loading directory…</span>
          </div>
        )}

        {!loading && error && (
          <div className="mb__error">{error}</div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="mb__empty">This directory is empty.</div>
        )}

        {!loading && !error && items.length > 0 && (
          <table className="mb__table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="mb__col-date">Last Modified</th>
                <th className="mb__col-size">Size</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr
                  key={i}
                  className={`mb__row mb__row--${it.isDir ? 'dir' : 'file'}`}
                  onClick={() => handleItemClick(it)}
                >
                  <td className="mb__name">
                    <span className="mb__icon">
                      {it.isDir ? <FolderOutlined /> : getFileIcon(it.name)}
                    </span>
                    <span className="mb__label">{it.name}{it.isDir && '/'}</span>
                  </td>
                  <td className="mb__date">{it.date || '—'}</td>
                  <td className="mb__size">{it.size || (it.isDir ? '—' : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
